<script setup>
// [TODO] Use Teleport to move the modal to the end of the body
import {
  ref,
  computed,
  defineEmits,
  nextTick,
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const { ariaLabel, modalType, closeOnBackdropClick, onClose } = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  onClose: { type: Function, required: true },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });
const modalMask = ref(null);
const opener = ref(null);
const dialogLabel = ref('');

const modalClassName = computed(() => {
  const modalClassNameMap = {
    centered: '',
    'right-aligned': 'right-aligned',
  };

  return `modal-mask skip-context-menu ${modalClassNameMap[modalType] || ''}`;
});

// [TODO] Revisit this logic to use outside click directive
const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose();
};

const focusModal = async () => {
  const root = modalMask.value;
  if (!root) return;
  dialogLabel.value = '';
  const heading = root.querySelector('h1, h2, h3, [role="heading"]');
  if (!ariaLabel && heading?.textContent?.trim()) {
    dialogLabel.value = heading.textContent.trim();
  }
  await nextTick();
  if (!show.value || root !== modalMask.value) return;
  root.focus();
};

const handleTab = e => {
  const root = modalMask.value;
  if (!show.value || !root || e.target.closest?.('.modal-mask') !== root)
    return;
  const isTopmost =
    [...document.querySelectorAll('.modal-mask')].at(-1) === root;
  if (!isTopmost) return;
  const controls = [
    ...root.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ),
  ].filter(
    element =>
      !element.disabled &&
      !element.hidden &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.tabIndex >= 0 &&
      element.type !== 'hidden' &&
      element.getClientRects().length > 0 &&
      !element.matches(':disabled')
  );
  if (controls.length === 0) {
    e.preventDefault();
    root.focus();
    return;
  }
  const first = controls[0];
  const last = controls.at(-1);
  if (
    e.shiftKey &&
    (document.activeElement === first || document.activeElement === root)
  ) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
};

const restoreFocus = () => {
  const target = opener.value;
  opener.value = null;
  if (target?.isConnected && typeof target.focus === 'function') target.focus();
};

const activate = () => {
  opener.value = document.activeElement;
  nextTick(focusModal);
};

watch(show, visible => {
  if (visible) activate();
  else restoreFocus();
});

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = e => {
  if (e.defaultPrevented) return;
  const isTopmost =
    [...document.querySelectorAll('.modal-mask')].at(-1) === modalMask.value;
  const nativeDialogOpen = document.querySelector('dialog[open]');
  if (show.value && isTopmost && !nativeDialogOpen && e.code === 'Escape') {
    close();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }
  if (show.value && isTopmost && e.key === 'Tab') handleTab(e);
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

onMounted(() => {
  if (show.value) activate();
  if (import.meta.env.DEV && onClose && typeof onClose === 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      "[DEPRECATED] The 'onClose' prop is deprecated. Please use the 'close' event instead."
    );
  }
});

onBeforeUnmount(() => {
  if (show.value) restoreFocus();
});
</script>

<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="show"
        ref="modalMask"
        :class="modalClassName"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || dialogLabel || undefined"
        tabindex="-1"
        transition="modal"
        @mousedown="handleMouseDown"
      >
        <div
          class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu"
          :class="{
            'rounded-[var(--workspace-radius,20px)] w-full max-w-[552px]':
              !fullWidth,
            'items-center rounded-none flex h-full justify-center w-full':
              fullWidth,
            [size]: true,
          }"
          @mouse.stop
          @mousedown="event => event.stopPropagation()"
        >
          <Button
            v-if="showCloseButton"
            ghost
            slate
            icon="i-ph-x"
            :aria-label="$t('GENERAL.CLOSE')"
            class="absolute z-10 top-2 end-2"
            @click="close"
          />
          <slot />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss">
.modal-mask {
  @apply flex items-center justify-center bg-n-alpha-black2 dark:bg-n-modal-overlay backdrop-blur-[4px] z-[9990] h-full left-0 fixed top-0 w-full;

  .modal-container {
    &.medium {
      @apply max-w-[80%] w-[56.25rem];
    }
    &.large {
      @apply max-w-[90%] w-[75rem];
    }
    &.full-width {
      @apply w-[95%] max-w-[96rem];
    }
    &.tiny {
      @apply w-[320px];
    }
    // .content-box {
    //   @apply h-auto p-0;
    // }
    .content {
      @apply p-8;
    }

    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;

      a {
        @apply p-4;
      }
    }
  }
}

.modal-big {
  @apply w-full;
}

.modal-mask.right-aligned {
  @apply justify-end;

  .modal-container {
    @apply rounded-none h-full w-[30rem];
  }
}
// Guia Visor: entra em 200 ms a partir de scale .96; a saída é mais curta.
.modal-fade-enter-active {
  transition: opacity var(--motion-duration-modal) var(--motion-ease-enter);

  .modal-container {
    transition: transform var(--motion-duration-modal) var(--motion-ease-enter);
  }
}
.modal-fade-leave-active {
  transition: opacity var(--motion-duration-exit) var(--motion-ease-exit);
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  @apply opacity-0;

  .modal-container {
    transform: scale(0.96);
  }
}
</style>
